import { Component } from 'preact';
import AppHeader from './AppHeader';
import { BackButton } from './ui/navButtons';
import { TabBar } from './ui/TabBar';
import CreateDraftModal from './CreateDraftModal';
import RuneSpinner from './RuneSpinner';
import { UI } from '../utils/arena/uiSounds';
import { BOOSTER_SETS } from '../utils/arena/packsApi';
import { SET_UNLOCK_LEVELS, levelFromXp } from '../utils/arena/profileDefaults';
import {
  listDraftEvents, createDraftEvent, joinDraftEvent, leaveDraftEvent, cancelDraftEvent, inviteToDraft,
  subscribeToDraftEvents, DRAFT_STATUS, DRAFT_VISIBILITY, DRAFT_ENTRY_COST, keystoneCostForMode,
} from '../utils/arena/draftApi';
import { sendChatMessage } from '../utils/arena/chatApi';
import { CoinIcon, KeystoneIcon } from './ui/icons';
import DraftLobby from './DraftLobby';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD, COIN_COLOR,
  VIGNETTE, BEVELED_BTN, GOLD_BTN, DANGER_BTN,
  PANEL_STYLE, TAB_ACTIVE_TEXT, TAB_INACTIVE, TAB_BAR_STYLE,
  CornerPlating,
} from '../lib/medievalTheme';
import { t, k } from '../localization/t';
import { isFreePlay } from '../utils/freePlay';
import AmbientParticles from './AmbientParticles';

const TABS = [
  { key: 'public', label: k('Public Events') },
  { key: 'my', label: k('My Events') },
];

const STATUS_LABELS = {
  [DRAFT_STATUS.OPEN]: k('Open'),
  [DRAFT_STATUS.DRAFTING]: k('Drafting'),
  [DRAFT_STATUS.BUILDING]: k('Building'),
  [DRAFT_STATUS.TOURNAMENT]: k('In Progress'),
  [DRAFT_STATUS.COMPLETE]: k('Complete'),
  [DRAFT_STATUS.CANCELLED]: k('Cancelled'),
};

const STATUS_COLORS = {
  [DRAFT_STATUS.OPEN]: '#6dba6d',
  [DRAFT_STATUS.DRAFTING]: ACCENT_GOLD,
  [DRAFT_STATUS.BUILDING]: ACCENT_GOLD,
  [DRAFT_STATUS.TOURNAMENT]: '#d49243',
  [DRAFT_STATUS.COMPLETE]: TEXT_MUTED,
  [DRAFT_STATUS.CANCELLED]: '#c45050',
};

function formatEventTime(isoString) {
  const d = new Date(isoString);
  const now = new Date();
  const diffMs = d - now;
  if (diffMs < 0) return t('Started');
  if (diffMs < 60 * 1000) return t('Starting soon');
  if (diffMs < 60 * 60 * 1000) return t('In {n}m', { n: Math.ceil(diffMs / 60000) });
  if (diffMs < 24 * 60 * 60 * 1000) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default class DraftBrowser extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tab: 'public',
      events: [],
      loading: true,
      error: null,
      showCreate: false,
      joiningId: null,
      lobbyEventId: null,
    };
  }

  componentDidMount() {
    this.unsubDraft = subscribeToDraftEvents({
      joined: () => this.loadEvents(),
      left: () => this.loadEvents(),
      cancelled: () => this.loadEvents(),
    });
    this.loadEvents();
    if (this.props.activeDraftEventId) {
      this.setState({ lobbyEventId: this.props.activeDraftEventId });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.activeDraftEventId && this.props.activeDraftEventId !== prevProps.activeDraftEventId) {
      this.setState({ lobbyEventId: this.props.activeDraftEventId });
    }
  }

  componentWillUnmount() {
    this.unsubDraft?.();
  }

  loadEvents = async () => {
    this.setState({ loading: true, error: null });
    try {
      const events = await listDraftEvents();
      this.setState({ events: events || [], loading: false });
    } catch (err) {
      this.setState({ events: [], loading: false, error: err.message });
    }
  };

  handleCreate = async (params) => {
    const event = await createDraftEvent(params);
    if (params.invitePlayerIds?.length) {
      await inviteToDraft(event.id, params.invitePlayerIds);
      // Chat-delivered invite so it lands in the recipient's Social tab
      // conversation list with inline Accept/Decline buttons. The
      // Social tab renders ChatConversationList exclusively, so a
      // mail-only invite would be invisible.
      const setLabels = { beta: 'Beta', arthurian: 'Arthurian Legends', gothic: 'Gothic' };
      const setLabel = setLabels[event.setKey] || event.setKey;
      await Promise.all(
        params.invitePlayerIds.map((friendId) =>
          sendChatMessage(friendId, {
            type: 'draft-invite',
            body: 'invited you to a Draft!',
            metadata: {
              eventId: event.id,
              mode: event.mode || 'standard',
              setKey: event.setKey,
              setLabel,
              podSize: event.podSize,
              entryCost: event.entryCost,
              scheduledAt: event.scheduledAt,
            },
          }).catch((err) => console.warn('[draft] chat invite send failed:', err))
        )
      );
    }
    this.setState({ showCreate: false, lobbyEventId: event.id });
    this.loadEvents();
    this.props.onDraftJoined?.(event.id);
  };

  handleJoin = async (eventId, paymentMethod = 'coins') => {
    this.setState({ joiningId: eventId });
    try {
      await joinDraftEvent(eventId, { paymentMethod });
      this.setState({ joiningId: null, lobbyEventId: eventId });
      this.props.onDraftJoined?.(eventId);
    } catch (err) {
      this.setState({ joiningId: null });
      console.error('[DraftBrowser] join failed:', err);
    }
  };

  openLobby = (eventId) => {
    this.setState({ lobbyEventId: eventId });
  };

  closeLobby = () => {
    this.setState({ lobbyEventId: null });
  };

  handleLeave = async (eventId) => {
    try {
      await leaveDraftEvent(eventId);
      this.loadEvents();
    } catch (err) {
      console.error('[DraftBrowser] leave failed:', err);
    }
  };

  handleCancel = async (eventId) => {
    try {
      await cancelDraftEvent(eventId);
      this.loadEvents();
    } catch (err) {
      console.error('[DraftBrowser] cancel failed:', err);
    }
  };

  getFilteredEvents() {
    const { tab, events } = this.state;
    const { profile } = this.props;
    if (!events) return [];

    switch (tab) {
      case 'public':
        return events.filter((e) => e.visibility === DRAFT_VISIBILITY.PUBLIC && e.status === DRAFT_STATUS.OPEN);
      case 'my':
        return events.filter((e) =>
          e.creatorId === profile?.id ||
          e.participants?.some((p) => p.playerId === profile?.id)
        );
      default:
        return events;
    }
  }

  render() {
    const { profile, onBack, onToggleMailbox, mailboxUnreadCount, mailboxDropdown, onToggleFriends, friendListData } = this.props;
    const { tab, loading, error, showCreate, joiningId } = this.state;
    const filtered = this.getFilteredEvents();
    const emptyMessages = { public: t('No open public events'), my: t("You haven't joined any events") };

    return (
      <div className="fixed inset-0 z-50 flex flex-col overflow-hidden select-none" style={{ background: '#08080a' }}>
        <div className="absolute inset-0" style={{ background: `url('/hub-bg.webp') center/cover no-repeat`, filter: 'blur(3px)', transform: 'scale(1.02)' }} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,0.7) 100%)' }} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: VIGNETTE }} />
        <AmbientParticles />

        <AppHeader
          profile={profile}
          onToggleMailbox={onToggleMailbox}
          mailboxUnreadCount={mailboxUnreadCount}
          mailboxDropdown={mailboxDropdown}
          onToggleFriends={onToggleFriends}
          friendListData={friendListData}
          draftQueueDropdown={this.props.draftQueueDropdown}
        >
          <BackButton title={t('Back to Hub')} onClick={onBack} />
        </AppHeader>

        <div className="relative z-10 flex-1 flex flex-col overflow-hidden px-8 py-4">
          {/* Title bar */}
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold arena-heading" style={{ color: ACCENT_GOLD, textShadow: '0 2px 4px rgba(0,0,0,0.5), 0 0 20px rgba(200,160,60,0.15)' }}>
              {t('Draft Events')}
            </h1>
            <button
              type="button"
              className="px-5 py-2 text-sm font-semibold transition-all cursor-pointer"
              style={GOLD_BTN}
              data-sound={UI.CONFIRM}
              onClick={() => this.setState({ showCreate: true })}
            >
              {t('+ Create Event')}
            </button>
          </div>

          {/* Tabs */}
          <TabBar className="flex w-fit mb-4" style={TAB_BAR_STYLE} activeIndex={TABS.findIndex((item) => item.key === tab)}>
            {TABS.map((item) => (
              <button
                key={item.key}
                type="button"
                data-tab-item
                className="px-4 py-2 text-xs font-semibold cursor-pointer"
                style={tab === item.key ? TAB_ACTIVE_TEXT : TAB_INACTIVE}
                data-sound={UI.TAB}
                onClick={() => this.setState({ tab: item.key })}
              >
                {t(item.label)}
              </button>
            ))}
          </TabBar>

          {/* Event list */}
          <div className="flex-1 overflow-y-auto pr-1">
            {loading ? (
              <div className="flex justify-center items-center py-16">
                <RuneSpinner size={48} />
              </div>
            ) : error ? (
              <div className="text-center py-16 text-sm" style={{ color: '#c45050' }}>{error}</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-sm mb-2" style={{ color: TEXT_MUTED }}>
                  {emptyMessages[tab] || t('No events found')}
                </div>
                <div className="text-xs" style={{ color: TEXT_MUTED }}>{t('Create one to get started!')}</div>
              </div>
            ) : (
              <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(20cqw, 1fr))' }}>
                {filtered.map((event) => this.renderEventCard(event, joiningId))}
              </div>
            )}
          </div>
        </div>

        {showCreate ? (
          <CreateDraftModal
            profile={profile}
            friendListData={friendListData}
            onCreate={this.handleCreate}
            onClose={() => this.setState({ showCreate: false })}
          />
        ) : null}

        {this.state.lobbyEventId ? (
          <DraftLobby
            eventId={this.state.lobbyEventId}
            profile={profile}
            sorceryCards={this.props.sorceryCards}
            friendListData={friendListData}
            onClose={this.closeLobby}
            onLeft={() => { this.closeLobby(); this.loadEvents(); this.props.onDraftLeft?.(); }}
            onCancelled={() => { this.closeLobby(); this.loadEvents(); this.props.onDraftCancelled?.(); }}
            onDraftStarted={this.props.onDraftStarted}
          />
        ) : null}
      </div>
    );
  }

  renderEventCard(event, joiningId) {
    const { profile } = this.props;
    const isJoined = event.participants?.some((p) => p.playerId === profile?.id);
    const isFull = (event.participants?.length || 0) >= event.podSize;
    const entryCost = event.entryCost || DRAFT_ENTRY_COST;
    // Free play charges no entry fee.
    const freePlay = isFreePlay();
    const canAfford = freePlay || (profile?.coins || 0) >= entryCost;
    const ksCost = keystoneCostForMode(event.mode);
    const hasKeystone = !freePlay && (profile?.keystones || 0) >= ksCost && entryCost > 0;
    const playerLevel = profile ? levelFromXp(profile.xp || 0) : 1;
    const requiredLevel = SET_UNLOCK_LEVELS[event.setKey] || 1;
    const setUnlocked = playerLevel >= requiredLevel;
    const canJoin = !isJoined && !isFull && event.status === DRAFT_STATUS.OPEN && setUnlocked;
    const joining = joiningId === event.id;
    const setLabel = BOOSTER_SETS[event.setKey]?.label || event.setKey;

    return (
      <div
        key={event.id}
        className="relative p-4 rounded transition-all"
        style={{
          ...PANEL_STYLE,
          cursor: isJoined ? 'pointer' : 'default',
        }}
        onClick={isJoined ? () => this.props.onOpenDraftLobby?.(event.id) : undefined}
      >
        <CornerPlating position="top-left" />
        <CornerPlating position="top-right" />
        <CornerPlating position="bottom-left" />
        <CornerPlating position="bottom-right" />

        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="text-sm font-semibold" style={{ color: TEXT_PRIMARY }}>{t('{set} Draft', { set: setLabel })}</div>
            <div className="text-xs" style={{ color: TEXT_MUTED }}>{t('by {name}', { name: event.creatorName || t('Unknown') })}</div>
          </div>
          <span
            className="text-[0.625cqw] font-bold px-2 py-0.5 rounded"
            style={{ color: STATUS_COLORS[event.status] || TEXT_MUTED, background: 'rgba(0,0,0,0.3)' }}
          >
            {STATUS_LABELS[event.status] ? t(STATUS_LABELS[event.status]) : event.status}
          </span>
        </div>

        <div className="flex items-center gap-4 text-xs mb-3" style={{ color: TEXT_BODY }}>
          <span>{formatEventTime(event.scheduledAt)}</span>
          <span>{t('{count}/{max} players', { count: event.participants?.length || 0, max: event.podSize })}</span>
          {freePlay ? null : <span className="flex items-center gap-0.5" style={{ color: COIN_COLOR }}>{entryCost}<CoinIcon size={10} /></span>}
        </div>

        {!isJoined && !setUnlocked && event.status === DRAFT_STATUS.OPEN ? (
          <button
            type="button"
            className="w-full py-1.5 text-xs font-semibold rounded"
            style={{ ...BEVELED_BTN, opacity: 0.45, cursor: 'not-allowed' }}
            disabled
          >
            {t('Unlocks at Level {level}', { level: requiredLevel })}
          </button>
        ) : canJoin ? (
          hasKeystone ? (
            // Player has a Keystone: split-button row. Primary = spend
            // the Keystone (free entry), secondary = fall back to gold.
            // We auto-prefer Keystone because the whole point of the
            // currency is free draft entries.
            <div className="flex gap-2">
              <button
                type="button"
                className="flex-1 py-1.5 text-xs font-semibold transition-all rounded flex items-center justify-center gap-1"
                style={GOLD_BTN}
                data-sound={UI.CONFIRM}
                disabled={joining}
                onClick={(e) => { e.stopPropagation(); this.handleJoin(event.id, 'keystone'); }}
                title={ksCost === 1 ? t('Spend 1 Keystone for a free entry') : t('Spend {n} Keystones for a free entry', { n: ksCost })}
              >
                {joining ? t('Joining...') : <>{t('Join · {n}', { n: ksCost })} <KeystoneIcon size={11} /></>}
              </button>
              <button
                type="button"
                className="py-1.5 px-2 text-xs font-semibold transition-all rounded flex items-center justify-center gap-1"
                style={{
                  ...BEVELED_BTN,
                  opacity: canAfford ? 1 : 0.45,
                  cursor: canAfford ? 'pointer' : 'not-allowed',
                }}
                disabled={joining || !canAfford}
                onClick={(e) => { e.stopPropagation(); this.handleJoin(event.id, 'coins'); }}
                title={canAfford ? t('Pay {n} gold instead', { n: entryCost }) : t('You need {n} coins to use gold', { n: entryCost })}
              >
                {entryCost} <CoinIcon size={11} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="w-full py-1.5 text-xs font-semibold transition-all rounded"
              style={{
                ...(canAfford ? GOLD_BTN : BEVELED_BTN),
                opacity: canAfford ? 1 : 0.45,
                cursor: canAfford ? 'pointer' : 'not-allowed',
              }}
              data-sound={canAfford ? UI.CONFIRM : undefined}
              disabled={joining || !canAfford}
              onClick={(e) => { e.stopPropagation(); this.handleJoin(event.id, 'coins'); }}
              title={canAfford ? undefined : t('You need {n} coins to join', { n: entryCost })}
            >
              {joining ? t('Joining...') : freePlay ? t('Join Draft') : canAfford ? t('Join Draft · {n}', { n: entryCost }) : t('Need {n} coins', { n: entryCost })}
              {!joining && !freePlay ? <CoinIcon size={11} /> : null}
            </button>
          )
        ) : isJoined && event.status === DRAFT_STATUS.OPEN ? (
          <div className="flex gap-2 mt-1">
            <button
              type="button"
              className="flex-1 py-1.5 text-xs font-semibold transition-all cursor-pointer rounded"
              style={GOLD_BTN}
              data-sound={UI.CONFIRM}
              onClick={(e) => { e.stopPropagation(); this.openLobby(event.id); }}
            >
              {t('Open Lobby')}
            </button>
            {event.creatorId === profile?.id ? (
              <button
                type="button"
                className="py-1.5 px-3 text-xs transition-all cursor-pointer rounded"
                style={DANGER_BTN}
                data-sound={UI.CANCEL}
                onClick={(e) => { e.stopPropagation(); this.handleCancel(event.id); }}
              >
                {t('Cancel')}
              </button>
            ) : (
              <button
                type="button"
                className="py-1.5 px-3 text-xs transition-all cursor-pointer rounded"
                style={{ ...BEVELED_BTN, color: TEXT_BODY }}
                data-sound={UI.CANCEL}
                onClick={(e) => { e.stopPropagation(); this.handleLeave(event.id); }}
              >
                {t('Leave')}
              </button>
            )}
          </div>
        ) : null}
      </div>
    );
  }
}
